import React, { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { db, doc, getDoc } from '../services/firebase';

const EmptyCartState = () => (
  <div style={{ 
    textAlign: 'center', 
    margin: '80px 0 60px 0',
    maxWidth: '400px',
    marginLeft: 'auto',
    marginRight: 'auto'
  }}>
    <div style={{
      width: '120px',
      height: '120px',
      border: '2px solid #e8e8e8',
      borderRadius: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 32px',
      background: '#fafafa'
    }}>
      <span style={{
        fontSize: '48px',
        color: '#ccc',
        fontWeight: '300'
      }}>
        ×
      </span>
    </div>
    <h2 style={{ 
      fontWeight: '600', 
      fontSize: '24px', 
      color: '#181818', 
      marginBottom: '12px',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    }}>
      Your Cart is Empty
    </h2>
    <p style={{ 
      color: '#666', 
      fontSize: '16px', 
      marginBottom: '40px',
      lineHeight: 1.5
    }}>
      Add items from our menu to get started
    </p>
  </div>
);

const CartItemRow = ({ item, updateQuantity, removeFromCart, isMobile }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingQuantity, setPendingQuantity] = useState(item.quantity);
  
  const getVariantText = (item) => {
    const parts = [];
    if (item.weightDisplay && item.weightDisplay !== 'Standard') {
      parts.push(item.weightDisplay);
    } else if (item.selectedWeight && item.selectedWeight !== 'standard') {
      parts.push(item.selectedWeight);
    }
    if (item.selectedBeanType && item.selectedBeanType !== 'default') {
      parts.push(item.selectedBeanType);
    }
    return parts.join(' - ');
  };

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity < 1) return;
    setIsUpdating(true);
    setPendingQuantity(newQuantity);
    
    try {
      await updateQuantity(item.id, item.selectedWeight, item.selectedBeanType, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
      setPendingQuantity(item.quantity);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = async () => {
    setIsUpdating(true);
    try {
      await removeFromCart(item.id, item.selectedWeight, item.selectedBeanType);
    } catch (error) {
      console.error('Failed to remove item:', error);
      setIsUpdating(false);
    }
  };

  return (
    <div 
      className={isMobile ? 'cart-item-row' : ''}
      style={{
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'row' : 'row',
        padding: isMobile ? '20px 0' : '24px 0',
        borderBottom: '1px solid #f0f0f0',
        transition: 'all 0.3s ease',
        opacity: isUpdating ? 0.7 : 1,
        gap: isMobile ? '0' : '0',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
      {/* Product Image */}
      <div style={{
        width: isMobile ? '80px' : '80px',
        height: isMobile ? '80px' : '80px',
        background: '#f8f8f8',
        border: '1px solid #e8e8e8',
        borderRadius: '0',
        marginRight: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '0' : '0',
        flexShrink: 0,
        overflow: 'hidden',
        alignSelf: isMobile ? 'flex-start' : 'center'
      }}>
        <img 
          src={item.imageUrl || item.image || `/images/${item.id}.png`}
          alt={item.name} 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            e.target.onerror = null;
            if (e.target.src !== `/images/${item.id}.png`) {
              e.target.src = `/images/${item.id}.png`;
            }
          }}
        />
      </div>
      
      {/* Product Info */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'flex-start',
        flexDirection: 'column',
        width: '100%',
        gap: isMobile ? '12px' : '0',
        minWidth: 0,
        overflow: 'hidden'
      }}>
        {/* Name and Variant */}
        <div style={{ 
          flex: 1,
          width: isMobile ? '100%' : 'auto'
        }}>
          <h3 style={{
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: '600',
            color: '#181818',
            margin: '0 0 4px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            wordBreak: 'break-word',
            maxWidth: '100%'
          }}>
            {item.name}
          </h3>
          {getVariantText(item) && (
            <p style={{
              fontSize: '14px',
              color: '#666',
              margin: '0 0 4px 0',
              fontStyle: 'italic'
            }}>
              {getVariantText(item)}
            </p>
          )}
          <p style={{
            fontSize: '13px',
            color: '#888',
            margin: '0'
          }}>
            QAR {item.price.toFixed(2)} per {item.weightDisplay && item.weightDisplay !== 'Standard' ? item.weightDisplay : 'unit'}
          </p>
        </div>
        
        {/* Dotted Line - Hide on mobile */}
        {!isMobile && (
          <div style={{
            flex: 1,
            height: '1px',
            borderBottom: '1px dotted #ddd',
            margin: '0 24px',
            minWidth: '40px'
          }} />
        )}
        
        {/* Controls Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: isMobile ? '8px' : '12px',
          marginTop: isMobile ? '8px' : '0',
          flexWrap: 'wrap'
        }}>
          {/* Quantity Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            margin: isMobile ? '0' : '0 24px 0 0'
          }}>
          <button 
            onClick={() => handleQuantityChange((isUpdating ? pendingQuantity : item.quantity) - 1)}
            disabled={isUpdating || (isUpdating ? pendingQuantity : item.quantity) <= 1}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #e8e8e8',
              background: '#fff',
              borderRadius: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#666',
              transition: 'all 0.2s ease'
            }}
          >
            −
          </button>
          
          <span style={{
            minWidth: '40px',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '600',
            color: '#181818'
          }}>
            {isUpdating ? pendingQuantity : item.quantity}
          </span>
          
          <button 
            onClick={() => handleQuantityChange((isUpdating ? pendingQuantity : item.quantity) + 1)}
            disabled={isUpdating}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #e8e8e8',
              background: '#fff',
              borderRadius: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#666',
              transition: 'all 0.2s ease'
            }}
          >
            +
          </button>
          </div>
          
          {/* Price */}
          <div style={{
            minWidth: isMobile ? 'auto' : '100px',
            textAlign: isMobile ? 'left' : 'right',
            flexShrink: 0
          }}>
            <span style={{
              fontSize: isMobile ? '14px' : '18px',
              fontWeight: '700',
              color: '#181818',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap'
            }}>
              QAR {(item.price * (isUpdating ? pendingQuantity : item.quantity)).toFixed(2)}
            </span>
          </div>
          
          {/* Remove Button */}
          <button 
            onClick={handleRemove}
            disabled={isUpdating}
            style={{
              marginLeft: isMobile ? '0' : '24px',
              width: '36px',
              height: '36px',
              border: 'none',
              background: 'transparent',
              borderRadius: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '16px',
              color: '#999',
              transition: 'all 0.2s ease'
            }}
          >
            {isUpdating ? '...' : '×'}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderSummary = ({ totalItems, totalPrice, navigate, isMobile }) => {
  const [deliverySettings, setDeliverySettings] = useState({
    deliveryFee: 30,
    freeDeliveryThreshold: 100
  });
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  useEffect(() => {
    const fetchDeliverySettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setDeliverySettings({
            deliveryFee: data.deliveryFee || 30,
            freeDeliveryThreshold: data.freeDeliveryThreshold || 100
          });
        }
      } catch (err) {
        console.error('Error fetching delivery settings:', err);
      }
    };
    
    fetchDeliverySettings();
  }, []);

  const deliveryFee = totalPrice >= deliverySettings.freeDeliveryThreshold ? 0 : deliverySettings.deliveryFee;
  const remainingForFreeShipping = deliverySettings.freeDeliveryThreshold - totalPrice;

  const handleCheckout = () => {
    setIsCheckingOut(true);
    navigate('/checkout');
  };

  return (
    <div style={{
      background: '#f8f8f8',
      padding: isMobile ? '24px' : '32px',
      borderRadius: '0',
      border: '1px solid #e8e8e8',
      width: isMobile ? '100%' : 'auto'
    }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '600',
        margin: '0 0 24px 0',
        color: '#181818',
        textTransform: 'uppercase',
        letterSpacing: '1px'
      }}>
        Order Summary
      </h2>
      
      {/* Free Shipping Progress */}
      {remainingForFreeShipping > 0 && remainingForFreeShipping <= 50 && (
        <div style={{
          padding: '16px',
          background: '#fff',
          border: '1px solid #e8e8e8',
          marginBottom: '24px'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: '0 0 8px 0'
          }}>
            Add QAR {remainingForFreeShipping.toFixed(2)} more for free shipping
          </p>
          <div style={{
            width: '100%',
            height: '4px',
            background: '#f0f0f0',
            borderRadius: '0'
          }}>
            <div style={{
              width: `${Math.min((totalPrice / deliverySettings.freeDeliveryThreshold) * 100, 100)}%`,
              height: '100%',
              background: '#333',
              borderRadius: '0',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}
      
      {/* Summary Breakdown */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Subtotal ({totalItems} {totalItems === 1 ? 'item' : 'items'})
          </span>
          <span style={{ fontSize: '14px', color: '#181818' }}>
            QAR {totalPrice.toFixed(2)}
          </span>
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Shipping
          </span>
          <span style={{ fontSize: '14px', color: '#181818' }}>
            {deliveryFee === 0 ? (
              <span>
                <span style={{ textDecoration: 'line-through', color: '#999' }}>
                  QAR {deliverySettings.deliveryFee.toFixed(2)}
                </span>
                <span style={{ marginLeft: '8px', color: '#4CAF50', fontWeight: '600' }}>
                  FREE
                </span>
              </span>
            ) : (
              `QAR ${deliveryFee.toFixed(2)}`
            )}
          </span>
        </div>
        
        <div style={{
          height: '1px',
          background: '#e8e8e8',
          margin: '16px 0'
        }} />
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ 
            fontSize: '16px', 
            fontWeight: '600',
            color: '#181818',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Total
          </span>
          <span style={{ 
            fontSize: '20px', 
            fontWeight: '700',
            color: '#181818',
            letterSpacing: '0.5px'
          }}>
            QAR {(totalPrice + deliveryFee).toFixed(2)}
          </span>
        </div>
      </div>
      
      {/* Checkout Button */}
      <button 
        onClick={handleCheckout}
        disabled={isCheckingOut || totalItems === 0}
        style={{
          width: '100%',
          padding: '16px 24px',
          background: '#333',
          color: '#fff',
          border: 'none',
          borderRadius: '0',
          fontSize: '14px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          opacity: isCheckingOut ? 0.7 : 1
        }}
      >
        {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
      </button>
      
      {/* Security Note */}
      <p style={{
        fontSize: '12px',
        color: '#888',
        textAlign: 'center',
        margin: '16px 0 0 0',
        fontStyle: 'italic'
      }}>
        Secure checkout with SSL encryption
      </p>
    </div>
  );
};

const CartPage = () => {
  const { cartItems, removeFromCart, updateQuantity, totalItems, totalPrice, loading } = useCart();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    if (!loading && cartItems.length === 0) {
      // Don't auto-redirect, let users see empty state
    }
  }, [cartItems, loading]);

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          fontSize: '16px',
          color: '#666',
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile-specific styles */}
      <style>{`
        * {
          box-sizing: border-box;
        }
        
        @media (max-width: 768px) {
          html, body {
            overflow-x: hidden !important;
            max-width: 100% !important;
          }
          .cart-mobile-container {
            padding: 20px 16px 80px !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
          }
          .cart-mobile-item {
            padding: 16px 0 !important;
            border-bottom: 1px solid #f0f0f0 !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
          }
          .cart-mobile-title {
            font-size: 24px !important;
            margin-bottom: 16px !important;
          }
          .cart-mobile-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            max-width: 100% !important;
          }
          .cart-item-row {
            max-width: 100% !important;
            overflow-x: hidden !important;
          }
          /* Prevent overflow from all child elements */
          .cart-mobile-container * {
            max-width: 100% !important;
          }
        }
      `}</style>
      
      <Header />
      
      <div 
        className={isMobile ? 'cart-mobile-container' : ''}
        style={{
          maxWidth: isMobile ? '100%' : '1200px',
          margin: '0 auto',
          padding: isMobile ? '20px 16px 60px' : '40px 24px 80px',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
        {/* Breadcrumb */}
        <button
          onClick={() => navigate('/catalogue')}
          style={{
            background: 'none',
            border: 'none',
            color: '#666',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '32px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
        >
          ← Continue Shopping
        </button>
        
        {/* Page Title */}
        <h1 
          className={isMobile ? 'cart-mobile-title' : ''}
          style={{
            fontSize: isMobile ? '24px' : '32px',
            fontWeight: '600',
            margin: '0 0 8px 0',
            color: '#181818',
            textTransform: 'uppercase',
            letterSpacing: isMobile ? '1px' : '2px'
          }}>
          Your Cart
        </h1>
        
        <div style={{
          height: '2px',
          background: '#e8e8e8',
          margin: '0 0 48px 0'
        }} />
        
        {cartItems.length === 0 ? (
          <>
            <EmptyCartState />
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => navigate('/catalogue')}
                style={{
                  padding: '16px 32px',
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0',
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                BROWSE CATALOGUE
              </button>
            </div>
          </>
        ) : (
          <div 
            className={isMobile ? 'cart-mobile-grid' : ''}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
              gap: isMobile ? '32px' : '48px',
              alignItems: 'flex-start'
            }}>
            {/* Cart Items */}
            <div>
              {cartItems.map((item, index) => (
                <CartItemRow
                  key={`${item.id}-${item.selectedWeight?.label || item.selectedWeight || ''}-${item.selectedBeanType || ''}`}
                  item={item}
                  updateQuantity={updateQuantity}
                  removeFromCart={removeFromCart}
                  isMobile={isMobile}
                />
              ))}
            </div>
            
            {/* Order Summary */}
            <OrderSummary
              totalItems={totalItems}
              totalPrice={totalPrice}
              navigate={navigate}
              isMobile={isMobile}
            />
          </div>
        )}
      </div>
      
      <Footer />
    </>
  );
};

export default CartPage;